if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.bindMediaNav();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.bindThumbnailClicks();
        this.enableThumbnailMouseScroll();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      bindMediaNav() {
        if (!this.elements.viewer) return;

        this.elements.viewer.querySelectorAll('.product__media-nav').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.cycleMedia(Number(button.dataset.dir || 1));
          });
        });
      }

      cycleMedia(direction) {
        const items = Array.from(this.elements.viewer.querySelectorAll('.product__media-item'));
        if (items.length < 2) return;

        const activeIndex = items.findIndex((item) => item.classList.contains('is-active'));
        const current = activeIndex >= 0 ? activeIndex : 0;
        const nextIndex = (current + direction + items.length) % items.length;
        const nextMediaId = items[nextIndex]?.dataset.mediaId;
        if (nextMediaId) this.setActiveMedia(nextMediaId, false);
      }

      bindThumbnailClicks() {
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.addEventListener('click', (event) => {
          const thumbnailItem = event.target.closest('[data-target]');
          if (!thumbnailItem || !this.elements.thumbnails.contains(thumbnailItem)) return;
          if (this.elements.thumbnails.dataset.dragMoved === 'true') return;

          event.preventDefault();
          this.setActiveMedia(thumbnailItem.dataset.target, false);
        });
      }

      enableThumbnailMouseScroll() {
        const thumbnailList = this.elements.thumbnails?.querySelector('[id^="Slider-Thumbnails"]');
        if (!thumbnailList) return;

        // Convert vertical mouse wheel to horizontal scroll
        thumbnailList.addEventListener(
          'wheel',
          (event) => {
            if (thumbnailList.scrollWidth <= thumbnailList.clientWidth) return;

            const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
            if (!delta) return;

            event.preventDefault();
            thumbnailList.scrollLeft += delta;
          },
          { passive: false }
        );

        // Drag-to-scroll without breaking thumbnail clicks
        let isPointerDown = false;
        let isDragging = false;
        let startX = 0;
        let startScrollLeft = 0;

        thumbnailList.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'touch') return;
          isPointerDown = true;
          isDragging = false;
          startX = event.clientX;
          startScrollLeft = thumbnailList.scrollLeft;
          this.elements.thumbnails.dataset.dragMoved = 'false';
        });

        thumbnailList.addEventListener('pointermove', (event) => {
          if (!isPointerDown) return;

          const walk = event.clientX - startX;
          if (!isDragging && Math.abs(walk) < 6) return;

          if (!isDragging) {
            isDragging = true;
            this.elements.thumbnails.dataset.dragMoved = 'true';
            thumbnailList.setPointerCapture?.(event.pointerId);
          }

          thumbnailList.scrollLeft = startScrollLeft - walk;
        });

        const stopDragging = (event) => {
          if (!isPointerDown) return;
          isPointerDown = false;

          if (isDragging) {
            thumbnailList.releasePointerCapture?.(event.pointerId);
            // Keep click blocked only for this pointer cycle
            window.setTimeout(() => {
              if (this.elements.thumbnails) this.elements.thumbnails.dataset.dragMoved = 'false';
            }, 0);
          }

          isDragging = false;
        };

        thumbnailList.addEventListener('pointerup', stopDragging);
        thumbnailList.addEventListener('pointercancel', stopDragging);
        thumbnailList.addEventListener('pointerleave', (event) => {
          if (isPointerDown && !isDragging) {
            isPointerDown = false;
          }
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
